import { useCallback, useEffect, useState } from "react";
import { Plural, Trans, useLingui } from "@lingui/macro";
import { toast } from "sonner";
import { Button } from "@app/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@app/components/ui/tooltip";
import { Check, Loader2, MessageSquareQuote, Minus, Quote, X } from "lucide-react";
import API from "@app/services/api";

/**
 * Ce que les cliniciens ont dit de CE champ, lu là où le champ se modifie.
 *
 * Le geste du médecin et la définition du champ vivaient dans deux écrans qui ne se
 * parlaient pas : `/laboratoire/corrections` lit tout le journal à l'échelle du corpus, et
 * cet écran-ci édite les champs et leurs exemples. Personne ne pouvait donc répondre à la
 * seule question qui fait avancer un prompt — « qu'est-ce que les médecins ont reproché à ce
 * champ, et qu'est-ce que j'en fais ». Ce panneau est ce chaînon.
 *
 * Quatre lectures, dans l'ordre où elles se décident :
 *
 *  - **les gestes**, dans les mêmes nombres que le laboratoire des corrections.
 *  - **le vocabulaire**, c'est-à-dire les valeurs que les médecins ont écrites, chacune
 *    passée par la porte d'énumération que l'extraction applique elle-même. Trois verdicts,
 *    et celui du milieu est la raison d'être du panneau : « écrasée » veut dire que la
 *    valeur n'est pas rejetée mais RÉÉCRITE en un terme plus grossier, silencieusement.
 *  - **les justifications**, la phrase du médecin posée À CÔTÉ de la valeur qu'elle explique.
 *    Le laboratoire des corrections publie ces phrases sans jamais publier une valeur ; ce
 *    panneau publiait les valeurs sans les phrases. Ni l'une ni l'autre ne tranche seule
 *    entre « le terme manque à la liste » et « la description envoyait ailleurs ».
 *  - **les propositions d'exemple**, l'extrait de source qu'un médecin a pris le temps de
 *    désigner. Les promouvoir est une décision humaine, une par une.
 *
 * Chargé À L'OUVERTURE et pas au montage : une ontologie porte cinquante champs, et ouvrir
 * l'écran ne doit pas déclencher cinquante requêtes pour un panneau que le lecteur ouvrira
 * une fois.
 */

/** Le verdict de la porte d'énumération sur une valeur écrite par un clinicien. */
type VocabularyVerdict = "declared" | "collapsed" | "rejected" | null;

interface VocabularyRow {
  value: string;
  count: number;
  verdict: VocabularyVerdict;
  /** La valeur déclarée sur laquelle la porte écrase celle-ci. Seulement pour « collapsed ». */
  normalizes_to: string | null;
}

interface JustificationRow {
  /** La valeur que le médecin a écrite, et celle qu'il remplaçait. */
  value: string;
  previous_value: string;
  error_class: string | null;
  target_field: string | null;
  note: string;
  at: string | null;
}

/** Une mesure : ce qu'une expérience terminée a dit de ce champ. */
interface Measurement {
  experiment_id: string;
  experiment_name: string;
  dataset_id: string;
  /** PROJECTION (the dated journal replayed); anything else is the retired flat aggregation. */
  scored_surface: string;
  as_of: string | null;
  at: string | null;
  mean_f1: number | null;
  n: number | null;
}

interface Quality {
  latest: Measurement | null;
  previous: Measurement | null;
  /** Nul quand il n'y a pas deux mesures COMPARABLES — même surface, même spécialité. */
  delta: number | null;
}

interface ProposalRow {
  id: string;
  input: string;
  output: unknown;
  explanation: string;
  proposed_by: string;
  created_at: string | null;
}

interface Digest {
  field_key: string;
  gestures: { validated: number; corrected: number; flagged: number };
  error_classes: Record<string, number>;
  vocabulary: VocabularyRow[];
  allowed_values: string[];
  justifications: JustificationRow[];
  quality: Quality;
  proposals: ProposalRow[];
  proposals_total: number;
}

function VerdictBadge({ row }: { row: VocabularyRow }) {
  const { t } = useLingui();
  if (row.verdict === "rejected") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            <Trans>Rejetée</Trans>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <Trans>
            Aucune valeur déclarée ne correspond : l'extraction qui produirait ce terme le
            jetterait. Ajoutez-le à la liste, ou reformulez la description du champ.
          </Trans>
        </TooltipContent>
      </Tooltip>
    );
  }
  if (row.verdict === "collapsed") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-900">
            <Trans>Écrasée sur « {row.normalizes_to} »</Trans>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <Trans>
            La valeur n'est pas rejetée, elle est réécrite : l'extraction rend le terme
            déclaré, plus grossier, et rien ne signale la substitution. C'est la nuance que
            l'ontologie ne sait pas exprimer.
          </Trans>
        </TooltipContent>
      </Tooltip>
    );
  }
  if (row.verdict === "declared") {
    return (
      <span
        className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
        aria-label={t`Valeur déjà déclarée`}
      >
        <Trans>Déclarée</Trans>
      </span>
    );
  }
  return null;
}

export default function ClinicianFeedbackPanel({
  stackId,
  fieldId,
  fieldKey,
  readOnly = false,
  onShotsChanged,
}: {
  stackId: string;
  fieldId: string;
  fieldKey: string | null;
  readOnly?: boolean;
  /** Remonté après une promotion : la liste d'exemples du champ a changé. */
  onShotsChanged?: () => void;
}) {
  const { t } = useLingui();
  const [digest, setDigest] = useState<Digest | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busyProposalId, setBusyProposalId] = useState<string | null>(null);
  const [rerunning, setRerunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get({
        path: `/ontology-stack/${stackId}/fields/${fieldId}/clinician-feedback`,
      });
      if (res.ok && res.data) {
        setDigest(res.data as Digest);
        setFailed(false);
      } else {
        setFailed(true);
      }
    } catch {
      // Affiché, jamais avalé : un panneau vide dirait « aucun médecin n'a rien dit », ce qui
      // est exactement la conclusion qu'une lecture en échec ne permet pas de tirer.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [stackId, fieldId]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (proposalId: string, action: "promote" | "dismiss") => {
    setBusyProposalId(proposalId);
    try {
      const path =
        action === "promote"
          ? `/ontology-stack/${stackId}/fields/${fieldId}/shots/from-proposal/${proposalId}`
          : `/ontology-stack/${stackId}/fields/${fieldId}/proposals/${proposalId}/dismiss`;
      const res = await API.post({ path, body: {} });
      if (!res.ok) {
        toast.error(res.error || t`L'action a échoué`);
        return;
      }
      toast.success(
        action === "promote" ? t`Exemple créé à partir du retour` : t`Proposition écartée`,
      );
      await load();
      if (action === "promote") onShotsChanged?.();
    } finally {
      setBusyProposalId(null);
    }
  };

  // Rejouer la DERNIÈRE mesure, à l'identique sauf l'ontologie.
  //
  // Ne lance rien : la nouvelle mesure est créée en attente et le lecteur l'ouvre là où se
  // trouvent déjà le bouton de démarrage, le réveil du GPU et le flux de progression. Deux
  // endroits pour orchestrer un run long, c'est un endroit de trop.
  const rerun = async (measurement: Measurement) => {
    setRerunning(true);
    try {
      const res = await API.post({
        path: `/dataset/${measurement.dataset_id}/experiments/${measurement.experiment_id}/rerun`,
        body: {},
      });
      if (!res.ok) {
        toast.error(res.error || t`La nouvelle mesure n'a pas pu être créée`);
        return;
      }
      toast.success(t`Nouvelle mesure créée — ouvrez-la pour la lancer`);
    } finally {
      setRerunning(false);
    }
  };

  if (!fieldKey) {
    return (
      <p className="text-sm text-slate-500">
        <Trans>
          Ce champ n'a pas encore de clé : aucun retour clinicien ne peut lui être rattaché.
        </Trans>
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        <Trans>Lecture des retours...</Trans>
      </div>
    );
  }

  if (failed || !digest) {
    return (
      <p className="text-sm text-slate-500">
        <Trans>
          Les retours de ce champ n'ont pas pu être lus. Le panneau est vide pour cette
          raison, et non parce qu'aucun médecin ne s'est prononcé.
        </Trans>
      </p>
    );
  }

  const { gestures, vocabulary, justifications, quality, proposals, proposals_total } = digest;
  const totalGestures = gestures.validated + gestures.corrected + gestures.flagged;
  const toWiden = vocabulary.filter(
    (row) => row.verdict === "rejected" || row.verdict === "collapsed",
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-5">
        {/* Les gestes — les mêmes nombres que /laboratoire/corrections, par construction. */}
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="inline-flex items-center gap-1.5 text-slate-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100">
              <Check className="h-3 w-3 text-green-700" />
            </span>
            <Plural value={gestures.validated} one="# validé" other="# validés" />
          </span>
          <span className="inline-flex items-center gap-1.5 text-slate-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100">
              <X className="h-3 w-3 text-red-700" />
            </span>
            <Plural value={gestures.corrected} one="# corrigé" other="# corrigés" />
          </span>
          <span className="inline-flex items-center gap-1.5 text-slate-700">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100">
              <Minus className="h-3 w-3 text-slate-600" />
            </span>
            <Plural value={gestures.flagged} one="# signalé" other="# signalés" />
          </span>
          {totalGestures === 0 && (
            <span className="text-slate-500">
              <Trans>Aucun geste sur ce champ dans votre périmètre.</Trans>
            </span>
          )}
        </div>

        {/* Le verdict : ce que la dernière mesure dit de ce champ, et le mouvement depuis la
            précédente. Placé tout en haut parce que c'est ce qui dit si le travail plus bas a
            servi — sans lui, l'écran se termine sur une modification et il faut aller chercher
            un nombre ailleurs, ce qui en pratique veut dire ne pas regarder.
            La surface est NOMMÉE : un F1 d'agrégation à plat et un F1 de projection datée ne
            décrivent pas le même moteur, et les confondre ferait lire un changement de moteur
            comme un progrès. */}
        {quality.latest && (
          <div className="rounded-lg border border-primary/20 bg-blue-50 px-3 py-2.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-sm text-slate-700">
                <Trans>Dernière mesure</Trans>
              </span>
              <span className="text-lg font-semibold text-slate-900">
                {quality.latest.mean_f1 === null ? "—" : quality.latest.mean_f1.toFixed(2)}
              </span>
              {quality.delta !== null && (
                <span
                  className={
                    quality.delta >= 0
                      ? "text-sm font-medium text-green-700"
                      : "text-sm font-medium text-red-700"
                  }
                >
                  {quality.delta >= 0 ? "+" : ""}
                  {quality.delta.toFixed(2)}
                </span>
              )}
              <span className="text-xs text-slate-500">
                {quality.latest.scored_surface === "PROJECTION" ? (
                  <Trans>projection datée</Trans>
                ) : (
                  <Trans>agrégation à plat (retirée)</Trans>
                )}
                {quality.latest.n !== null && <> · {quality.latest.n} <Trans>dossiers</Trans></>}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-xs text-slate-500">{quality.latest.experiment_name}</span>
              {quality.previous === null && (
                <span className="text-xs text-slate-500">
                  <Trans>
                    Pas de mesure comparable avant celle-ci : l'écart ne peut pas être calculé.
                  </Trans>
                </span>
              )}
              {/* The server refuses to replay a flat measurement: that engine is retired. */}
              {!readOnly && quality.latest.scored_surface === "PROJECTION" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  disabled={rerunning}
                  onClick={() => void rerun(quality.latest as Measurement)}
                >
                  {rerunning && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  <Trans>Remesurer après modification</Trans>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Le vocabulaire. Placé avant les exemples parce que c'est le levier mesuré : élargir
            une liste de valeurs change l'extraction de tous les dossiers, un exemple change
            le prompt d'un champ. */}
        {vocabulary.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-900">
              <Trans>Ce que les médecins ont écrit</Trans>
            </h4>
            {toWiden.length > 0 && (
              <p className="mt-1 text-xs text-slate-500">
                <Trans>
                  {toWiden.length} terme(s) que la liste déclarée ne sait pas rendre tels
                  quels. C'est la cause la plus fréquente d'un champ qui reste faux.
                </Trans>
              </p>
            )}
            <ul className="mt-2 space-y-1.5">
              {vocabulary.map((row) => (
                <li
                  key={row.value}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm"
                >
                  <span className="font-mono text-xs text-slate-500">×{row.count}</span>
                  <span className="min-w-0 flex-1 break-words text-slate-800">{row.value}</span>
                  <VerdictBadge row={row} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pourquoi ils l'ont écrit.
            Placé entre le vocabulaire et les exemples parce que c'est ce qui tranche entre les
            deux gestes possibles : la phrase du médecin dit si le terme manque à la liste ou si
            la description du champ l'envoyait ailleurs. Le laboratoire des corrections publie
            ces phrases sans jamais publier une valeur ; ce panneau publiait les valeurs sans les
            phrases. Aucune des deux moitiés ne décide seule. Une liste vide n'affiche pas une
            section qui ne dirait que « aucune ». */}
        {justifications.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-900">
              <Trans>Pourquoi ils l'ont corrigé</Trans>
            </h4>
            <ul className="mt-2 space-y-2">
              {justifications.map((justification, index) => (
                <li
                  key={`${justification.at}-${index}`}
                  className="rounded-md border border-slate-200 px-2.5 py-2 text-sm"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    {justification.previous_value && (
                      <>
                        <span className="text-slate-500 line-through">
                          {justification.previous_value}
                        </span>
                        <span className="text-slate-400" aria-hidden>
                          →
                        </span>
                      </>
                    )}
                    <span className="font-medium text-slate-900">{justification.value}</span>
                    {justification.error_class && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        {justification.error_class}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-slate-700">
                    {justification.note}
                  </p>
                  {justification.target_field && (
                    <p className="mt-1 text-xs text-slate-500">
                      <Trans>Aurait dû aller dans</Trans> :{" "}
                      <span className="font-mono">{justification.target_field}</span>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Les propositions d'exemple. */}
        <div>
          <h4 className="text-sm font-medium text-slate-900">
            <Trans>Exemples proposés par les médecins ({proposals_total})</Trans>
          </h4>
          {proposals.length === 0 ? (
            <p className="mt-1 text-xs text-slate-500">
              <Trans>
                Aucune proposition en attente. Une proposition ne devient un exemple que si
                quelqu'un la promeut depuis l'écran d'ontologie — rien n'est automatique. La
                file ne se remplit plus : un exemple exige un extrait verbatim du document, et
                le sélecteur de passages qui le fournissait a été retiré.
              </Trans>
            </p>
          ) : (
            <ul className="mt-2 space-y-3">
              {proposals.map((proposal) => (
                <li key={proposal.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start gap-2">
                    <Quote className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                    <p className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm text-slate-700">
                      {proposal.input}
                    </p>
                  </div>
                  <p className="mt-2 text-sm">
                    <span className="text-slate-500">
                      <Trans>Valeur attendue</Trans> :{" "}
                    </span>
                    <span className="font-mono text-slate-900">
                      {typeof proposal.output === "string"
                        ? proposal.output
                        : JSON.stringify(proposal.output)}
                    </span>
                  </p>
                  {proposal.explanation && (
                    <p className="mt-1 text-xs italic text-slate-500">{proposal.explanation}</p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-500">{proposal.proposed_by}</span>
                    {!readOnly && (
                      <div className="ml-auto flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyProposalId === proposal.id}
                          onClick={() => void act(proposal.id, "dismiss")}
                        >
                          <Trans>Écarter</Trans>
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyProposalId === proposal.id}
                          onClick={() => void act(proposal.id, "promote")}
                          className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          {busyProposalId === proposal.id ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                          ) : (
                            <MessageSquareQuote className="mr-1 h-4 w-4" />
                          )}
                          <Trans>Ajouter comme exemple</Trans>
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
